<!-- Copyright (C) 2025-2026 Skylark Drones -->
<template>
  <v-card v-bind="$attrs" rounded="xl" hover>
    <v-row
      align="center"
      justify="space-between"
      style="background-color: #0000000d"
      class="pa-4 flex-nowrap"
      no-gutters
    >
      <v-col lg="6" md="5" sm="4" class="overflow-hidden">
        <div
          class="text-truncate text-h6 font-weight-medium primary-font-color--text"
        >
          {{ $props.dock.call_sign }}
        </div>
        <div class="text-truncate text-body-2 secondary-font-color--text">
          {{ dockLocation }}
        </div>
      </v-col>

      <v-col cols="6" class="d-flex align-center justify-end">
        <div
          class="rounded-pill pa-2 d-flex align-center text-no-wrap overflow-hidden marquee"
          :class="state.bgColor"
        >
          <div class="marquee-static d-flex align-center mr-1">
            <profile-image-avatar
              v-if="state.avatar"
              :image-src="state.avatar.profilePicture"
              :name="state.avatar.name"
              :fallback-icon="state.avatar.fallbackIcon"
              :text-props="{ style: { fontSize: '12px' } }"
              size="24"
              class="mr-1"
            />
            <v-icon small color="white" class="mr-1">
              {{ state.icon }}
            </v-icon>
          </div>
          <div ref="marquee" class="marquee-track" :style="marqueeVars">
            <div
              ref="content"
              class="marquee-content text-body-2 white--text font-weight-bold"
              :class="{ animate: shouldAnimate }"
            >
              <span>
                {{ state.text }}
              </span>
              <span v-if="dockOfflineTime">
                <span class="mx-1">&middot;</span>
                {{ dockOfflineTime }}
              </span>
            </div>
          </div>
        </div>
        <dock-actions-menu
          :dock="dock"
          @edit="$emit('edit', $event)"
          @stop-livestreams="stopAllStreams"
        />
      </v-col>
    </v-row>
    <v-row
      align="center"
      justify="space-around"
      class="pa-4"
      no-gutters
      style="position: relative"
    >
      <v-overlay absolute :value="!$props.dockState?.online" color="white" />
      <!-- Dock -->
      <v-col
        cols="4"
        class="d-flex flex-column align-center"
        style="position: relative"
      >
        <v-img height="135" width="135" contain :src="dock.picture_url" />

        <div
          class="text-truncate text-body-2 mt-2 secondary-font-color--text font-weight-bold text-center"
        >
          {{ dock.model_name }}
        </div>

        <!-- Wrapped in a div to apply position absolute correctly -->
        <!-- Otherwise the position absolute was apply to v-menu inside hms-error-island -->
        <div style="position: absolute; top: 0; right: 0">
          <hms-error-island
            concise
            :hms-messages="$props.dockState?.hms"
            :source="source.DOCK_CARD"
          />
        </div>
      </v-col>

      <!-- Drone -->
      <v-col
        cols="4"
        class="d-flex flex-column align-center"
        style="position: relative"
      >
        <div v-if="dock.drone?.model_name" style="width: 100%">
          <v-img
            height="135"
            width="135"
            contain
            :src="dock.drone.picture_url"
          />
          <div
            class="d-flex align-center mt-2"
            :class="
              $props.dockState?.online ? 'justify-start' : 'justify-center'
            "
            style="width: 100%"
          >
            <div
              class="text-truncate text-body-2 secondary-font-color--text font-weight-bold"
              style="max-width: 100%"
            >
              {{ dock.drone.model_name }}
            </div>

            <template v-if="$props.dockState?.online">
              <span class="text-body-2 secondary-font-color--text mx-1">|</span>
              <v-icon small :color="batteryIconColor">
                {{ batteryIcon }}
              </v-icon>
              <span
                class="text-body-2 font-weight-bold"
                :class="[
                  !!$props.dockState?.drone_charging ? 'ml-1' : '',
                  `${batteryIconColor}--text`
                ]"
                style="max-width: 30px"
              >
                {{ droneBattery.toFixed(0) }}%
              </span>
            </template>
          </div>

          <!-- Wrapped in a div to apply position absolute correctly -->
          <!-- Otherwise the position absolute was apply to v-menu inside hms-error-island -->
          <div style="position: absolute; top: 0; right: 0">
            <hms-error-island
              concise
              :hms-messages="droneState?.hms"
              :source="source.DOCK_CARD"
            />
          </div>
        </div>
        <div v-else class="d-flex flex-column align-center">
          <div style="height: 135px" class="d-flex align-center">
            <v-icon size="90">dmo-drone</v-icon>
          </div>
          <span
            class="mt-2 text-truncate text-body-2 error--text font-weight-bold"
          >
            Not Bound
          </span>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { isEmpty } from 'lodash'

import { DOCK_RAINFALL, DRONE_MODE_CODE } from '@/constants/dock'
import {
  getBatteryIcon,
  getBatteryIconColor
} from '@/components/dock/common/battery'
import HmsErrorIsland from '@/components/dock/primary-feed/HmsErrorIsland.vue'
import DockActionsMenu from './DockActionsMenu.vue'
import ProfileImageAvatar from '@/components/common/ProfileImageAvatar.vue'
import { source } from '@/utils/analytics/abstraction/eventEnums'
import { stopLivestream } from '@/components/dock/common/feed'
import { mapMutations } from 'vuex'

export default {
  name: 'DockCard',

  components: {
    HmsErrorIsland,
    DockActionsMenu,
    ProfileImageAvatar
  },

  props: {
    dock: {
      type: Object,
      required: true
    },
    dockState: {
      type: Object,
      required: false
    }
  },

  data() {
    return {
      source,
      windSpeedThreshold: 12,
      rainFallThreshold: DOCK_RAINFALL.LIGHT_RAIN,
      containerWidth: 0,
      contentWidth: 0
    }
  },

  computed: {
    droneState() {
      return this.dockState?.drone
    },
    dockLocation() {
      return this.$props.dock?.location ?? 'Location N/A'
    },
    lastConnectedTimestamp() {
      return (
        this.$props.dockState?.last_connected_at ??
        this.$props.dock.last_connected_at
      )
    },
    dockOfflineTime() {
      const lastConnected = this.lastConnectedTimestamp

      if (!lastConnected || this.$props.dockState?.online) {
        return ''
      }

      const lastConnectedAt = this.$dayjs(lastConnected)

      const now = this.$dayjs()
      const minutesDiff = now.diff(lastConnectedAt, 'minute')
      if (minutesDiff < 60) {
        return `${Math.max(minutesDiff, 1)}m ago`
      }

      const hoursDiff = now.diff(lastConnectedAt, 'hour')
      if (hoursDiff < 24) {
        return `${hoursDiff}h ago`
      }

      if (hoursDiff < 48) {
        return `Yesterday, ${lastConnectedAt.format('hh:mm A')}`
      }

      const daysDiff = now.diff(lastConnectedAt, 'day')
      if (daysDiff < 7) {
        return `${daysDiff}d ago`
      }

      return `Since ${lastConnectedAt.format('DD MMM YY')}’`
    },
    state() {
      if (!this.$props.dockState?.online) {
        return {
          icon: 'mdi-cancel',
          text: 'Offline',
          bgColor: 'error'
        }
      }

      if (this.isExecutingMission) {
        return {
          icon: 'mdi-vector-polyline',
          text: 'On Mission',
          bgColor: 'primary'
        }
      }

      if (this.isManualFlying) {
        const pilot = this.$props.dockState.flight_authority

        let name, profilePicture, fallbackIcon
        if (isEmpty(pilot)) {
          fallbackIcon = 'mdi-account'
        } else {
          name = pilot.name
          profilePicture = pilot.profile_picture
        }

        return {
          icon: 'mdi-airplane',
          text: 'Flying',
          bgColor: 'info',
          avatar: {
            name,
            profilePicture,
            fallbackIcon
          }
        }
      }

      if (this.isLowBattery) {
        return {
          icon: 'dmo-drone',
          text: 'Battery Low',
          bgColor: 'error'
        }
      }

      if (this.isHeavyRainFall) {
        return {
          icon: 'dmo-downpour',
          text: 'Rain - N/A',
          bgColor: 'error'
        }
      }

      if (this.isHighWind) {
        return {
          icon: 'dmo-wind',
          text: 'Gust - N/A',
          bgColor: 'error'
        }
      }

      if (this.isDockOnBackupBattery) {
        return {
          icon: 'mdi-circle',
          text: 'Ready To Fly',
          bgColor: 'amber'
        }
      }

      return {
        icon: 'mdi-circle',
        text: 'Ready To Fly',
        bgColor: 'success'
      }
    },
    isManualFlying() {
      return [
        DRONE_MODE_CODE.MANUAL_FLIGHT,
        DRONE_MODE_CODE.VIRTUAL_STICK_STATE,
        DRONE_MODE_CODE.TAKEOFF_PREPARATION,
        DRONE_MODE_CODE.TAKEOFF_PREPARATION_COMPLETED,
        DRONE_MODE_CODE.AUTOMATIC_TAKEOFF,
        DRONE_MODE_CODE.PANORAMIC_PHOTOGRAPHY,
        DRONE_MODE_CODE.AUTO_RETURNING_TO_HOME,
        DRONE_MODE_CODE.AUTOMATIC_LANDING,
        DRONE_MODE_CODE.FORCED_LANDING,
        DRONE_MODE_CODE.THREE_BLADE_LANDING
      ].includes(this.droneState?.mode_code)
    },
    isExecutingMission() {
      return [
        DRONE_MODE_CODE.WAYLINE_FLIGHT,
        DRONE_MODE_CODE.LIVE_FLIGHT_CONTROLS
      ].includes(this.droneState?.mode_code)
    },
    isHeavyRainFall() {
      if (!this.$props.dock?.rainfall) return false

      return this.$props.dockState?.rainfall > this.rainFallThreshold
    },
    isHighWind() {
      const windSpeed = Math.max(
        this.droneState?.wind_speed ?? 0,
        this.$props.dockState?.wind_speed ?? 0
      )
      if (!windSpeed) return false

      return windSpeed > this.windSpeedThreshold
    },
    droneBattery() {
      return (
        this.droneState?.capacity_percent ||
        this.$props.dockState?.drone_battery_percent
      )
    },
    isLowBattery() {
      if (!this.droneBattery) return false

      return (
        this.droneBattery < this.droneState.low_battery_warning_threshold ?? 30
      )
    },
    isDockOnBackupBattery() {
      return this.$props.dockState.backup_battery
    },
    batteryIcon() {
      return getBatteryIcon(
        this.droneBattery,
        !!this.$props.dockState?.drone_charging
      )
    },
    batteryIconColor() {
      return getBatteryIconColor(
        this.droneBattery,
        !!this.$props.dockState?.drone_charging
      )
    },
    shouldAnimate() {
      // adding some buffer to trigger animation eagerly
      return this.contentWidth > this.containerWidth + 5
    },
    marqueeVars() {
      const distance = this.contentWidth - this.containerWidth + 15
      const speedPxPerSecond = 8
      const durationSeconds = Math.max(distance / speedPxPerSecond, 1)

      return {
        '--marquee-distance': `${distance}px`,
        '--marquee-duration': `${durationSeconds}s`
      }
    }
  },

  mounted() {
    this.checkOverflow()
    window.addEventListener('resize', this.checkOverflow)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkOverflow)
  },

  watch: {
    state() {
      this.checkOverflow()
    }
  },

  methods: {
    ...mapMutations(['updateNotificationPayload']),
    async stopAllStreams() {
      try {
        const activeFeeds = []

        if (this.dockState?.drone?.active_feeds) {
          activeFeeds.push(...Object.values(this.dockState.drone.active_feeds))
        }
        if (this.dockState?.active_feeds) {
          activeFeeds.push(...Object.values(this.dockState.active_feeds))
        }

        await Promise.all(
          activeFeeds.map(feed =>
            stopLivestream(this.$props.dock.sn, feed.video_id)
          )
        )

        this.updateNotificationPayload({
          message: 'Successfully stopped all dock feeds!',
          code: 201,
          timeout: 5000
        })
      } catch (error) {
        console.error(error)
        this.updateNotificationPayload({
          message: 'Unable to stop all dock feeds!',
          code: 400,
          timeout: 5000
        })
      }
    },
    checkOverflow() {
      this.$nextTick(() => {
        if (this.$refs.marquee && this.$refs.content) {
          this.containerWidth = this.$refs.marquee.clientWidth
          this.contentWidth = this.$refs.content.scrollWidth
        }
      })
    }
  }
}
</script>

<style scoped>
.marquee {
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
}

.marquee-track {
  flex: 1 1 auto;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
}

.marquee-content {
  display: inline-block;
}

.marquee-content.animate {
  animation: marquee var(--marquee-duration) linear infinite;
}

@keyframes marquee {
  0%,
  10% {
    transform: translateX(0);
  }
  50%,
  60% {
    transform: translateX(calc(-1 * var(--marquee-distance)));
  }
  90%,
  100% {
    transform: translateX(0);
  }
}
</style>
